import { toast } from "sonner";
import type { SearchableValuePickerOption } from "@/components/shared/searchable-value-picker";
import type {
  TenantAiProviderConnectionRecordDto,
  TenantAiProviderModelCatalogEntryDto,
} from "@/lib/api/generated/control-api.schemas";
import type {
  AgentModelConfigRecord,
  AgentModelConfigValue,
  AgentRuntimeConnectorAccessPolicy,
  AgentRuntimePolicy,
  AgentThinkingDefault,
  JsonObject,
} from "../../../../../packages/shared-types/src/index.js";
import {
  DEFAULT_AGENT_MEMORY_POLICY,
  DEFAULT_AGENT_TOOL_ALLOWLIST,
} from "../../../../../packages/shared-types/src/index.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ExecutionClass = AgentRuntimePolicy["executionClass"];

export type ModelSlotKey =
  | "model"
  | "imageModel"
  | "imageGenerationModel"
  | "pdfModel"
  | "subagentsModel"
  | "heartbeatModel"
  | "compactionModel";

export interface ModelSlotState {
  primary: string;
  fallbacks: string[];
}

export interface PolicyFormState {
  thinkingDefault: AgentThinkingDefault;
  allowUserThinkingOverride: boolean;
  executionClass: ExecutionClass;
  enabledWorkflowKeys: string[];
  enabledConnectorKeys: string[];
  allowedToolNames: string[];
  runtimeConnectorAccessJson: string;
  dangerousToolDeny: string[];
  allowCrossSummary: boolean;
  sessionMemoryEnabled: boolean;
  memoryFlushEnabled: boolean;
  memoryDreamingEnabled: boolean;
  memoryDreamingFrequency: string;
  activeMemoryEnabled: boolean;
  memoryEmbeddingModel: string;
  memoryEmbeddingDimensions: string;
  memoryPolicyJson: string;
  responseStyleJson: string;
  modelSlots: Record<ModelSlotKey, ModelSlotState>;
  modelEntriesJson: string;
  modelParamsJson: string;
  rawJson: string;
  advancedOpen: boolean;
}

export const MODEL_SLOT_DEFS: {
  key: ModelSlotKey;
  label: string;
  hasFallbacks: boolean;
}[] = [
  { key: "model", label: "Primary Model", hasFallbacks: true },
  { key: "imageModel", label: "Image Model", hasFallbacks: true },
  { key: "imageGenerationModel", label: "Image Generation Model", hasFallbacks: true },
  { key: "pdfModel", label: "PDF Model", hasFallbacks: true },
  { key: "subagentsModel", label: "Subagent Model", hasFallbacks: true },
  { key: "heartbeatModel", label: "Heartbeat Model Override", hasFallbacks: false },
  { key: "compactionModel", label: "Compaction Model Override", hasFallbacks: false },
];

export const EXECUTION_CLASS_OPTIONS: { value: ExecutionClass; label: string }[] = [
  { value: "chat_only", label: "Chat Only" },
  { value: "approved_actions", label: "Approved Actions" },
  { value: "operator_maintenance", label: "Operator Maintenance" },
];

export const THINKING_DEFAULT_OPTIONS: { value: AgentThinkingDefault; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "minimal", label: "Minimal" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "Extra High" },
  { value: "adaptive", label: "Adaptive" },
];

export const MEMORY_EMBEDDING_DIMENSION_OPTIONS = ["768", "1536", "3072"] as const;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPrimary(value?: AgentModelConfigValue): string {
  if (!value) return "";
  return typeof value === "string" ? value : value.primary ?? "";
}

function readFallbacks(value?: AgentModelConfigValue): string[] {
  if (!value || typeof value === "string") return [];
  return value.fallbacks ?? [];
}

function readModelSlot(value?: AgentModelConfigValue): ModelSlotState {
  return { primary: readPrimary(value), fallbacks: readFallbacks(value) };
}

function toModelConfigValue(
  primary: string,
  fallbacks: string[],
): AgentModelConfigValue | undefined {
  const p = primary.trim();
  const f = Array.from(new Set(fallbacks.map((e) => e.trim()).filter(Boolean)));
  if (!p && f.length === 0) return undefined;
  return {
    ...(p ? { primary: p } : {}),
    ...(f.length > 0 ? { fallbacks: f } : {}),
  };
}

function normalizeModelPickerValue(value: string | undefined): string {
  return value?.trim() ?? "";
}

function splitModelPickerGroup(value: string): string | undefined {
  const trimmed = value.trim();
  const slash = trimmed.indexOf("/");
  if (slash <= 0 || slash >= trimmed.length - 1) return undefined;
  return trimmed.slice(0, slash).trim() || undefined;
}

function normalizeProviderId(value: string | undefined | null): string {
  return value?.trim().toLowerCase() ?? "";
}

function readJsonObject(value: unknown): JsonObject | null {
  return isRecord(value) ? (value as JsonObject) : null;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readPositiveIntegerString(value: unknown): string {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return String(value);
  }
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return /^[1-9]\d*$/.test(trimmed) ? trimmed : "";
}

export function humanizeProviderLabel(value: string | undefined | null): string {
  const normalized = normalizeProviderId(value);
  if (!normalized) return "";
  return normalized
    .split(/[-_.]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function readConnectionCapabilityType(
  record: TenantAiProviderConnectionRecordDto,
): string {
  const metadata =
    record.metadata && typeof record.metadata === "object" && !Array.isArray(record.metadata)
      ? (record.metadata as Record<string, unknown>)
      : null;
  const capabilityType =
    typeof metadata?.capabilityType === "string"
      ? metadata.capabilityType.toLowerCase()
      : "ai_model";
  return capabilityType === "web_search" ? "web_search" : "ai_model";
}

function readMemorySearchPolicy(memoryPolicy: unknown): JsonObject | null {
  const policy = readJsonObject(memoryPolicy);
  return readJsonObject(policy?.memorySearch);
}

function readActiveMemoryEnabled(memoryPolicy: unknown): boolean {
  const policy = readJsonObject(memoryPolicy);
  if (policy?.enabled === false) return false;
  const activeMemory = readJsonObject(policy?.activeMemory);
  return activeMemory?.enabled !== false;
}

function readSessionMemoryEnabled(memoryPolicy: unknown): boolean {
  const policy = readJsonObject(memoryPolicy);
  if (policy?.enabled === false) return false;
  const sessionMemory = readJsonObject(policy?.sessionMemory);
  return sessionMemory?.enabled !== false;
}

function readMemoryFlushEnabled(memoryPolicy: unknown): boolean {
  const policy = readJsonObject(memoryPolicy);
  if (policy?.enabled === false) return false;
  const memoryFlush = readJsonObject(policy?.memoryFlush);
  return memoryFlush?.enabled !== false;
}

function readMemoryDreamingEnabled(memoryPolicy: unknown): boolean {
  const policy = readJsonObject(memoryPolicy);
  if (policy?.enabled === false) return false;
  const dreaming = readJsonObject(policy?.dreaming);
  return dreaming?.enabled === true || policy?.dreaming === true;
}

function readMemoryDreamingFrequency(memoryPolicy: unknown): string {
  const policy = readJsonObject(memoryPolicy);
  const dreaming = readJsonObject(policy?.dreaming);
  return readString(dreaming?.frequency);
}

function readMemoryEmbeddingModel(memoryPolicy: unknown): string {
  const memorySearch = readMemorySearchPolicy(memoryPolicy);
  const explicit = readString(memorySearch?.embeddingModel) || readString(memorySearch?.modelRef);
  if (explicit) return explicit;

  const provider = readString(memorySearch?.provider).toLowerCase();
  const model = readString(memorySearch?.model);
  if (!provider || !model) return "";
  if (provider === "gemini") return `google/${model}`;
  return `${provider}/${model}`;
}

function readMemoryEmbeddingDimensions(memoryPolicy: unknown): string {
  const memorySearch = readMemorySearchPolicy(memoryPolicy);
  return (
    readPositiveIntegerString(memorySearch?.outputDimensionality) ||
    readPositiveIntegerString(memorySearch?.dimensions)
  );
}

function applyMemoryEmbeddingFormState(memoryPolicy: JsonObject, state: PolicyFormState): JsonObject {
  const next: JsonObject = { ...memoryPolicy };
  const existing = readJsonObject(next.memorySearch);
  const memorySearch: JsonObject = existing ? { ...existing } : {};
  const existingActiveMemory = readJsonObject(next.activeMemory);
  const activeMemory: JsonObject = existingActiveMemory ? { ...existingActiveMemory } : {};
  const existingSessionMemory = readJsonObject(next.sessionMemory);
  const sessionMemory: JsonObject = existingSessionMemory ? { ...existingSessionMemory } : {};
  const existingMemoryFlush = readJsonObject(next.memoryFlush);
  const memoryFlush: JsonObject = existingMemoryFlush ? { ...existingMemoryFlush } : {};
  const existingDreaming = readJsonObject(next.dreaming);
  const dreaming: JsonObject = existingDreaming ? { ...existingDreaming } : {};
  const embeddingModel = state.memoryEmbeddingModel.trim();
  const dimensions = readPositiveIntegerString(state.memoryEmbeddingDimensions);

  delete memorySearch.fallback;

  if (embeddingModel) {
    memorySearch.embeddingModel = embeddingModel;
    delete memorySearch.modelRef;
    delete memorySearch.provider;
    delete memorySearch.model;
  } else {
    delete memorySearch.embeddingModel;
    delete memorySearch.modelRef;
  }

  if (dimensions) {
    memorySearch.outputDimensionality = Number(dimensions);
    delete memorySearch.dimensions;
  } else {
    delete memorySearch.outputDimensionality;
    delete memorySearch.dimensions;
  }

  next.memorySearch = memorySearch;
  sessionMemory.enabled = state.sessionMemoryEnabled;
  next.sessionMemory = sessionMemory;
  memoryFlush.enabled = state.memoryFlushEnabled;
  next.memoryFlush = memoryFlush;
  dreaming.enabled = state.memoryDreamingEnabled;
  if (state.memoryDreamingFrequency.trim()) {
    dreaming.frequency = state.memoryDreamingFrequency.trim();
  } else {
    delete dreaming.frequency;
  }
  next.dreaming = dreaming;
  activeMemory.enabled = state.activeMemoryEnabled;
  next.activeMemory = activeMemory;
  return next;
}

// ---------------------------------------------------------------------------
// Policy <-> Form state conversion
// ---------------------------------------------------------------------------

export function emptyPolicy(): AgentRuntimePolicy {
  return {
    thinkingDefault: "medium",
    allowUserThinkingOverride: false,
    executionClass: "approved_actions",
    enabledWorkflowKeys: [],
    enabledConnectorKeys: [],
    allowedToolNames: [...DEFAULT_AGENT_TOOL_ALLOWLIST],
    runtimeConnectorAccess: {
      allowedBindingIds: [],
      allowedTargetKeysByBindingId: {},
      defaultTargetKeyByBindingId: {},
    },
    dangerousToolDeny: [],
    allowCrossConversationSummaryAccess: false,
    memoryPolicy: { ...DEFAULT_AGENT_MEMORY_POLICY },
    responseStyle: {},
  };
}

export function policyToFormState(p: AgentRuntimePolicy): PolicyFormState {
  const mc = p.modelConfig;
  const runtimeConnectorAccess = normalizeRuntimeConnectorAccessPolicy(p.runtimeConnectorAccess);
  const allowedToolNames = normalizeToolNameList(p.allowedToolNames ?? []);
  const defaultAllowedToolNames =
    allowedToolNames.length > 0
      ? allowedToolNames
      : [...DEFAULT_AGENT_TOOL_ALLOWLIST];
  return {
    thinkingDefault: p.thinkingDefault ?? "medium",
    allowUserThinkingOverride: p.allowUserThinkingOverride ?? false,
    executionClass: p.executionClass,
    enabledWorkflowKeys: p.enabledWorkflowKeys ?? [],
    enabledConnectorKeys: p.enabledConnectorKeys ?? [],
    allowedToolNames: defaultAllowedToolNames,
    runtimeConnectorAccessJson: JSON.stringify(runtimeConnectorAccess, null, 2),
    dangerousToolDeny: p.dangerousToolDeny ?? [],
    allowCrossSummary: p.allowCrossConversationSummaryAccess ?? false,
    sessionMemoryEnabled: readSessionMemoryEnabled(p.memoryPolicy),
    memoryFlushEnabled: readMemoryFlushEnabled(p.memoryPolicy),
    memoryDreamingEnabled: readMemoryDreamingEnabled(p.memoryPolicy),
    memoryDreamingFrequency: readMemoryDreamingFrequency(p.memoryPolicy),
    activeMemoryEnabled: readActiveMemoryEnabled(p.memoryPolicy),
    memoryEmbeddingModel: readMemoryEmbeddingModel(p.memoryPolicy),
    memoryEmbeddingDimensions: readMemoryEmbeddingDimensions(p.memoryPolicy),
    memoryPolicyJson: JSON.stringify(p.memoryPolicy ?? {}, null, 2),
    responseStyleJson: JSON.stringify(p.responseStyle ?? {}, null, 2),
    modelSlots: {
      model: readModelSlot(mc?.model),
      imageModel: readModelSlot(mc?.imageModel),
      imageGenerationModel: readModelSlot(mc?.imageGenerationModel),
      pdfModel: readModelSlot(mc?.pdfModel),
      subagentsModel: readModelSlot(mc?.subagents?.model),
      heartbeatModel: { primary: mc?.heartbeat?.model ?? "", fallbacks: [] },
      compactionModel: { primary: mc?.compaction?.model ?? "", fallbacks: [] },
    },
    modelEntriesJson: JSON.stringify(mc?.models ?? {}, null, 2),
    modelParamsJson: JSON.stringify(mc?.params ?? {}, null, 2),
    rawJson: JSON.stringify(p, null, 2),
    advancedOpen: false,
  };
}

export function formStateToPolicy(
  state: PolicyFormState,
  options?: { quiet?: boolean },
): AgentRuntimePolicy | null {
  const report = options?.quiet ? () => {} : (msg: string) => toast.error(msg);

  let memoryPolicy: JsonObject;
  let responseStyle: JsonObject;
  let runtimeConnectorAccess: AgentRuntimeConnectorAccessPolicy;
  try {
    memoryPolicy = JSON.parse(state.memoryPolicyJson) as JsonObject;
    if (!isRecord(memoryPolicy)) throw new Error("invalid");
  } catch {
    report("Invalid JSON in Memory Policy");
    return null;
  }
  try {
    responseStyle = JSON.parse(state.responseStyleJson) as JsonObject;
  } catch {
    report("Invalid JSON in Response Style");
    return null;
  }
  try {
    runtimeConnectorAccess = normalizeRuntimeConnectorAccessPolicy(
      JSON.parse(state.runtimeConnectorAccessJson) as AgentRuntimeConnectorAccessPolicy,
    );
  } catch {
    report("Invalid JSON in Runtime Connector Access");
    return null;
  }

  const modelConfig = assembleModelConfig(state, options);
  if (modelConfig === null) return null;
  memoryPolicy = applyMemoryEmbeddingFormState(memoryPolicy, state);

  return {
    thinkingDefault: state.thinkingDefault,
    allowUserThinkingOverride: state.allowUserThinkingOverride,
    executionClass: state.executionClass,
    enabledWorkflowKeys: state.enabledWorkflowKeys,
    enabledConnectorKeys: state.enabledConnectorKeys,
    allowedToolNames: normalizeToolNameList(state.allowedToolNames),
    runtimeConnectorAccess,
    dangerousToolDeny: state.dangerousToolDeny,
    allowCrossConversationSummaryAccess: state.allowCrossSummary,
    memoryPolicy,
    responseStyle,
    ...(modelConfig ? { modelConfig } : {}),
  };
}

function normalizeToolNameList(values: string[]): string[] {
  return Array.from(new Set(values.map((entry) => entry.trim()).filter(Boolean))).sort(
    (left, right) => left.localeCompare(right),
  );
}

function normalizeRuntimeConnectorAccessPolicy(
  value: AgentRuntimePolicy["runtimeConnectorAccess"],
): AgentRuntimeConnectorAccessPolicy {
  const allowedBindingIds = Array.from(
    new Set((value?.allowedBindingIds ?? []).map((entry) => entry.trim()).filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right));

  const allowedTargetKeysByBindingId: Record<string, string[]> = {};
  for (const bindingId of allowedBindingIds) {
    const targetKeys = Array.from(
      new Set(
        (value?.allowedTargetKeysByBindingId?.[bindingId] ?? [])
          .map((entry) => entry.trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right));
    allowedTargetKeysByBindingId[bindingId] = targetKeys;
  }

  const defaultTargetKeyByBindingId: Record<string, string> = {};
  for (const bindingId of allowedBindingIds) {
    const targetKey = value?.defaultTargetKeyByBindingId?.[bindingId]?.trim();
    if (targetKey && allowedTargetKeysByBindingId[bindingId]?.includes(targetKey)) {
      defaultTargetKeyByBindingId[bindingId] = targetKey;
    }
  }

  return {
    allowedBindingIds,
    allowedTargetKeysByBindingId,
    defaultTargetKeyByBindingId,
  };
}

// ---------------------------------------------------------------------------
// Model config assembly
// ---------------------------------------------------------------------------

function assembleModelConfig(
  state: PolicyFormState,
  options?: { quiet?: boolean },
): AgentModelConfigRecord | null | undefined {
  const report = options?.quiet ? () => {} : (msg: string) => toast.error(msg);

  let models: JsonObject;
  let runtimeParams: JsonObject;
  try {
    const parsed = JSON.parse(state.modelEntriesJson) as JsonObject;
    if (!isRecord(parsed)) throw new Error("invalid");
    models = parsed;
  } catch {
    report("Invalid JSON in Model Catalog Overrides");
    return null;
  }
  try {
    const parsed = JSON.parse(state.modelParamsJson) as JsonObject;
    if (!isRecord(parsed)) throw new Error("invalid");
    runtimeParams = parsed;
  } catch {
    report("Invalid JSON in Agent Model Params");
    return null;
  }

  const s = state.modelSlots;
  const config: AgentModelConfigRecord = {};

  const modelVal = toModelConfigValue(s.model.primary, s.model.fallbacks);
  if (modelVal) config.model = modelVal;

  const imageVal = toModelConfigValue(s.imageModel.primary, s.imageModel.fallbacks);
  if (imageVal) config.imageModel = imageVal;

  const imageGenVal = toModelConfigValue(
    s.imageGenerationModel.primary,
    s.imageGenerationModel.fallbacks,
  );
  if (imageGenVal) config.imageGenerationModel = imageGenVal;

  const pdfVal = toModelConfigValue(s.pdfModel.primary, s.pdfModel.fallbacks);
  if (pdfVal) config.pdfModel = pdfVal;

  const subagentsVal = toModelConfigValue(
    s.subagentsModel.primary,
    s.subagentsModel.fallbacks,
  );
  if (subagentsVal) config.subagents = { model: subagentsVal };

  if (s.heartbeatModel.primary.trim()) {
    config.heartbeat = { model: s.heartbeatModel.primary.trim() };
  }
  if (s.compactionModel.primary.trim()) {
    config.compaction = { model: s.compactionModel.primary.trim() };
  }

  if (Object.keys(models).length > 0) {
    config.models = models as NonNullable<AgentModelConfigRecord["models"]>;
  }
  if (Object.keys(runtimeParams).length > 0) {
    config.params = runtimeParams;
  }

  return Object.keys(config).length > 0 ? config : undefined;
}

// ---------------------------------------------------------------------------
// Model picker options builder
// ---------------------------------------------------------------------------

export function collectModelPickerOptions(params: {
  modelConfig?: AgentModelConfigRecord;
  currentValues: string[];
  modelEntriesJson: string;
  catalogModels?: TenantAiProviderModelCatalogEntryDto[];
  connectedProviderIds?: Set<string>;
  connectedProviderGroups?: Set<string>;
}): SearchableValuePickerOption[] {
  const options = new Map<string, SearchableValuePickerOption>();

  const add = (value: string | undefined, hint?: string) => {
    const normalized = normalizeModelPickerValue(value);
    if (!normalized) return;
    const group = splitModelPickerGroup(normalized);
    const existing = options.get(normalized);
    if (existing) {
      if (hint && !existing.hint) existing.hint = hint;
      if (!existing.group && group) existing.group = group;
      return;
    }
    options.set(normalized, {
      value: normalized,
      label: normalized,
      ...(hint ? { hint } : {}),
      ...(group ? { group } : {}),
    });
  };

  for (const entry of params.catalogModels ?? []) {
    const provider = normalizeModelPickerValue(entry.provider);
    const modelId = normalizeModelPickerValue(entry.id);
    if (!provider || !modelId) continue;

    const value = `${provider}/${modelId}`;
    const hints: string[] = [];
    const providerGroupId = normalizeProviderId(entry.providerGroupId);
    const isConnected =
      entry.selectable ||
      Boolean(entry.connectionId) ||
      params.connectedProviderIds?.has(provider) ||
      (providerGroupId.length > 0 && params.connectedProviderGroups?.has(providerGroupId));

    if (entry.name?.trim() && entry.name.trim() !== modelId) hints.push(entry.name.trim());
    if (typeof entry.contextWindow === "number" && entry.contextWindow > 0) {
      hints.push(`${Intl.NumberFormat("en-US").format(entry.contextWindow)} ctx`);
    }
    if (typeof entry.maxTokens === "number" && entry.maxTokens > 0) {
      hints.push(`${Intl.NumberFormat("en-US").format(entry.maxTokens)} max`);
    }
    if (entry.reasoning) hints.push("reasoning");
    if (entry.input?.includes("image")) hints.push("image");
    if (entry.input?.includes("document")) hints.push("document");
    if (providerGroupId && providerGroupId !== provider) {
      hints.push(`via ${humanizeProviderLabel(providerGroupId)}`);
    }
    if (!isConnected || entry.missingAuth) hints.push("provider not connected");
    add(value, hints.join(" · "));
  }

  for (const value of params.currentValues) {
    const normalized = normalizeModelPickerValue(value);
    add(value, normalized && !options.has(normalized) ? "current (not in catalog)" : undefined);
  }

  const mc = params.modelConfig;
  add(mc?.model && typeof mc.model === "string" ? mc.model : undefined);
  add(mc?.imageModel && typeof mc.imageModel === "string" ? mc.imageModel : undefined);
  add(
    mc?.imageGenerationModel && typeof mc.imageGenerationModel === "string"
      ? mc.imageGenerationModel
      : undefined,
  );
  add(mc?.pdfModel && typeof mc.pdfModel === "string" ? mc.pdfModel : undefined);
  add(
    mc?.subagents?.model && typeof mc.subagents.model === "string"
      ? mc.subagents.model
      : undefined,
  );
  add(mc?.heartbeat?.model);
  add(mc?.compaction?.model);

  for (const [rawKey, rawEntry] of Object.entries(mc?.models ?? {})) {
    const key = normalizeModelPickerValue(rawKey);
    if (!key) continue;
    const entry = isRecord(rawEntry) ? rawEntry : undefined;
    const hints: string[] = [];
    const alias = typeof entry?.alias === "string" ? entry.alias.trim() : "";
    if (alias) hints.push(`alias: ${alias}`);
    if (entry?.streaming === true) hints.push("streaming");
    if (entry?.params && isRecord(entry.params)) hints.push("params");
    add(key, hints.length > 0 ? hints.join(" · ") : undefined);
    if (alias) add(alias, `alias for ${key}`);
  }

  try {
  const parsed = JSON.parse(params.modelEntriesJson);
    if (isRecord(parsed)) {
      for (const [rawKey, rawEntry] of Object.entries(parsed)) {
        const key = normalizeModelPickerValue(rawKey);
        if (!key) continue;
        const entry = isRecord(rawEntry) ? rawEntry : undefined;
        const hints: string[] = [];
        const alias = typeof entry?.alias === "string" ? entry.alias.trim() : "";
        if (alias) hints.push(`alias: ${alias}`);
        if (entry?.streaming === true) hints.push("streaming");
        add(key, hints.length > 0 ? hints.join(" · ") : undefined);
        if (alias) add(alias, `alias for ${key}`);
      }
    }
  } catch {
    // Keep suggestions resilient while the advanced JSON editor is in-flight.
  }

  return [...options.values()].sort(
    (left: SearchableValuePickerOption, right: SearchableValuePickerOption) => {
      const leftGroup = left.group ?? "";
      const rightGroup = right.group ?? "";
      const groupCompare = leftGroup.localeCompare(rightGroup);
      if (groupCompare !== 0) return groupCompare;
      return left.value.localeCompare(right.value);
    },
  );
}

export function collectEmbeddingModelPickerOptions(params: {
  currentValue: string;
  catalogModels?: TenantAiProviderModelCatalogEntryDto[];
  connectedProviderIds?: Set<string>;
  connectedProviderGroups?: Set<string>;
}): SearchableValuePickerOption[] {
  const options = new Map<string, SearchableValuePickerOption>();

  const add = (value: string | undefined, hint?: string) => {
    const normalized = normalizeModelPickerValue(value);
    if (!normalized) return;
    const group = splitModelPickerGroup(normalized);
    const existing = options.get(normalized);
    if (existing) {
      if (hint && !existing.hint) existing.hint = hint;
      if (!existing.group && group) existing.group = group;
      return;
    }
    options.set(normalized, {
      value: normalized,
      label: normalized,
      ...(hint ? { hint } : {}),
      ...(group ? { group } : {}),
    });
  };

  for (const entry of params.catalogModels ?? []) {
    if (!isEmbeddingModelCatalogEntry(entry)) continue;

    const provider = normalizeModelPickerValue(entry.provider);
    const modelId = normalizeModelPickerValue(entry.id);
    if (!provider || !modelId) continue;

    const providerGroupId = normalizeProviderId(entry.providerGroupId);
    const isConnected =
      entry.selectable ||
      Boolean(entry.connectionId) ||
      params.connectedProviderIds?.has(provider) ||
      (providerGroupId.length > 0 && params.connectedProviderGroups?.has(providerGroupId));
    const hints: string[] = [];
    if (entry.name?.trim() && entry.name.trim() !== modelId) hints.push(entry.name.trim());
    if (typeof entry.contextWindow === "number" && entry.contextWindow > 0) {
      hints.push(`${Intl.NumberFormat("en-US").format(entry.contextWindow)} ctx`);
    }
    if (providerGroupId && providerGroupId !== provider) {
      hints.push(`via ${humanizeProviderLabel(providerGroupId)}`);
    }
    if (!isConnected || entry.missingAuth) hints.push("provider not connected");
    add(`${provider}/${modelId}`, hints.join(" · "));
  }

  const current = normalizeModelPickerValue(params.currentValue);
  add(current, current && !options.has(current) ? "current (not in catalog)" : undefined);

  return [...options.values()].sort(
    (left: SearchableValuePickerOption, right: SearchableValuePickerOption) => {
      const leftGroup = left.group ?? "";
      const rightGroup = right.group ?? "";
      const groupCompare = leftGroup.localeCompare(rightGroup);
      if (groupCompare !== 0) return groupCompare;
      return left.value.localeCompare(right.value);
    },
  );
}

function isEmbeddingModelCatalogEntry(entry: TenantAiProviderModelCatalogEntryDto): boolean {
  const inputs = entry.input ?? [];
  if (inputs.includes("embedding")) return true;
  const modelId = normalizeModelPickerValue(entry.id).toLowerCase();
  return modelId.startsWith("text-embedding-") || modelId.startsWith("gemini-embedding-");
}
